import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for infinite scroll functionality
 * @param {Function} fetchFn - Async function that fetches data, receives page number
 * @param {Object} options - Configuration options
 * @returns {Object} - Data, loading states, and control functions
 */
export const useInfiniteScroll = (fetchFn, options = {}) => {
  const {
    perPage = 20,
    initialPage = 1,
    threshold = 200, // pixels from bottom to trigger load
    enabled = true,
  } = options;

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Fetch data for a specific page
  const fetchPage = useCallback(async (pageNum, isLoadMore = false) => {
    if (!enabled) return;
    
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const result = await fetchFn(pageNum, perPage);
      
      const newItems = result.sessions || result.items || result.data || [];
      const pagination = result.pagination || {};
      
      if (isLoadMore) {
        setItems(prev => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
      
      setHasMore(pagination.has_more ?? newItems.length === perPage);
      setTotal(pagination.total ?? 0);
      setPage(pageNum);
      
    } catch (err) {
      console.error('Infinite scroll fetch error:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [fetchFn, perPage, enabled]);

  // Initial load
  useEffect(() => {
    if (enabled) {
      fetchPage(initialPage, false);
    }
  }, [enabled]); // Only run on mount or when enabled changes

  // Load more function
  const loadMore = useCallback(() => {
    if (!loadingMore && !loading && hasMore) {
      fetchPage(page + 1, true);
    }
  }, [loadingMore, loading, hasMore, page, fetchPage]);

  // Refresh/reload function
  const refresh = useCallback(() => {
    setItems([]);
    setPage(initialPage);
    setHasMore(true);
    fetchPage(initialPage, false);
  }, [fetchPage, initialPage]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: `${threshold}px` }
    );

    observer.observe(loadMoreRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loading, loadingMore, loadMore, threshold]);

  return {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    total,
    page,
    loadMore,
    refresh,
    loadMoreRef, // Attach this ref to a sentinel element at the bottom
    setItems, // For manual updates
  };
};

export default useInfiniteScroll;
